/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ADMIN_ROLE,
  SUPER_ADMIN_ROLE,
  PREFERRED_ROLE,
  PREFERRED_ADMIN_ROLE,
} from 'modules/common/constants/roles';
import {
  selectAssignedHotelGroups,
  selectHotelDropdownList,
  selectHotelGroups,
  selectHotelName,
  selectIsDemoUser,
  SelectIsHotelSetModified,
  selectIsSignedIn,
  selectsHotelForecast,
  selectTabValue,
  selectUserRole,
  selectVisualFilters,
  selectIsDemoModeActivated,
  selectTargetDate,
  selectTrendTargetDate,
  selectIsPrimaryHotelSetOverTime,
  selectTrendBookingDate,
  selectPaceBookingStyle,
  selectTrendBookingStyle,
  selectMergedHotelList,
  selectHotelSetNameUpdate,
  selectResetBookingDate,
  selectPartialHours,
  selectLastPartialBookingDate,
  selectTabList,
} from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { isEmpty } from 'modules/common/helpers/object';
import {
  getForecastMessage,
  getHotelDemoModeFocusOnValue,
  getHotelGroupFocusOnValue,
} from '../functions';
import {
  selectDateComparison,
  selectSummaryAlignmentToggle,
  selectSummaryBookingDate,
  selectSummaryCustomDate,
  selectSummaryEndDate,
  selectHotelFocusOn as selectSummaryHotelFocusOn,
  selectSummaryOtb,
  selectSummaryStartDate,
  selectSymmetricComparisonToggle,
} from '../components/tab-container/summary-tab/selectors';
import {
  selectWorkbookAlignmentToggle,
  selectHotelFocusOn as selectWorkbookHotelFocusOn,
  selectDateComparison as selectWorkbookDateComparison,
  selectWorkbookCustomDate,
  selectWorkbookStartDate,
  selectWorkbookEndDate,
  selectWorkbookBookingDate,
  selectSymmetricComparisonToggle as selectWorkbookSymmetricComparisonToggle,
  selectWorkbookOtb,
} from '../components/tab-container/workbook-report/selectors';
import {
  selectPaceAlignmentToggle,
  selectPaceBookingPaceWindow,
  selectPaceDateComparison,
  selectHotelFocusOn as selectPaceHotelFocusOn,
  selectPaceSymmetricComparisonToggle,
  selectPaceBookingDate,
} from '../components/tab-container/pace-tabs/selectors';
import {
  selectTrendHotelFocusOn,
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeAlignmentToggle,
  selectTrendTimeCustomDate,
  selectTrendTimeDateComparison,
  selectTrendTimeEndDate,
  selectTrendTimeStartDate,
} from '../components/tab-container/trend-tabs/selectors';
import { CURRENCY_TYPES } from '../constants';
import { getCurrencySchema } from '../components/tab-container/inner-filter/functions';
import { paceActions } from '../components/tab-container/pace-tabs/slice';
import { trendActions } from '../components/tab-container/trend-tabs/slice';
import { ENVIRONMENT } from '../../../config';
import { selectWebClickHotelFocusOn } from '../components/tab-container/web-click-tab/selectors';
import {
  evaluateHotelDateValidation,
  useSetSecondaryHotelSetOverTime,
} from '../utils/hotel-set-functions';
import { workbookActions } from '../components/tab-container/workbook-report/slice';
import {
  selectRegionalMapOtb,
  selectRegionMapBookingDate,
} from '../components/tab-container/regional-map-tab/selectors';
import { summaryActions } from '../components/tab-container/summary-tab/slice';
import { selectSchemaMapping } from '../../common/auth/selectors';
/**
 * Custom hook to set tab accessibility based on user role
 */
const UseTabContainer = () => {
  const dispatch = useDispatch();
  const userRole = useSelector(selectUserRole);
  const hotelList = useSelector(selectHotelDropdownList);
  const hotelGroups = useSelector(selectHotelGroups);
  const selectedHotelName = useSelector(selectHotelName);
  const assignedHotelGroups = useSelector(selectAssignedHotelGroups);
  const isDemoUser = useSelector(selectIsDemoUser);
  const summaryHotelFocusOn = useSelector(selectSummaryHotelFocusOn);
  const workbookHotelFocusOn = useSelector(selectWorkbookHotelFocusOn);
  const trendHotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const paceHotelFocusOn = useSelector(selectPaceHotelFocusOn);
  const webClicksHotelFocusOn = useSelector(selectWebClickHotelFocusOn);
  const tabValue = useSelector(selectTabValue);
  const visualFilters = useSelector(selectVisualFilters);
  const isHotelForecast = useSelector(selectsHotelForecast);
  const isSignIn = useSelector(selectIsSignedIn);
  const isSelectedHotelSetModified = useSelector(SelectIsHotelSetModified);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const targetDate = useSelector(selectTargetDate);
  const trendTargetDate = useSelector(selectTrendTargetDate);
  const alignmentToggle = useSelector(selectSummaryAlignmentToggle);
  const summaryDateComparison = useSelector(selectDateComparison);
  const trendDateComparison = useSelector(selectTrendTimeDateComparison);
  const trendAlignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const paceDateComparison = useSelector(selectPaceDateComparison);
  const paceAlignmentToggle = useSelector(selectPaceAlignmentToggle);
  const workbookAlignmentToggle = useSelector(selectWorkbookAlignmentToggle);
  const workbookDateComparison = useSelector(selectWorkbookDateComparison);
  const summaryCustomStartDate = useSelector(selectSummaryStartDate);
  const summaryCustomEndDate = useSelector(selectSummaryEndDate);
  const trendTimeCustomStartDate = useSelector(selectTrendTimeStartDate);
  const trendTimeCustomEndDate = useSelector(selectTrendTimeEndDate);
  const workbookCustomStartDate = useSelector(selectWorkbookStartDate);
  const workbookCustomEndDate = useSelector(selectWorkbookEndDate);
  const summaryCustomDate = useSelector(selectSummaryCustomDate);
  const workbookCustomDate = useSelector(selectWorkbookCustomDate);
  const trendTimeCustomDate = useSelector(selectTrendTimeCustomDate);
  const primaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const summaryBookingDate = useSelector(selectSummaryBookingDate);
  const regionalMapBookingDate = useSelector(selectRegionMapBookingDate);
  const trendBookingDate = useSelector(selectTrendBookingDate);
  const paceBookingDate = useSelector(selectPaceBookingDate);
  const workbookBookingDate = useSelector(selectWorkbookBookingDate);
  const summarySymmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const paceSymmetricComparisonToggle = useSelector(selectPaceSymmetricComparisonToggle);
  const trendSymmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const workBookSymmetricComparisonToggle = useSelector(selectWorkbookSymmetricComparisonToggle);
  const isSummaryCustomBooking = useSelector(selectSummaryOtb);
  const isPaceCustomBooking = useSelector(selectPaceBookingStyle);
  const isRegionCustomBooking = useSelector(selectRegionalMapOtb);
  const isTrendCustomBooking = useSelector(selectTrendBookingStyle);
  const isWorkbookCustomBooking = useSelector(selectWorkbookOtb);
  const bookingPaceWindow = useSelector(selectPaceBookingPaceWindow);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const setUpdate = useSelector(selectHotelSetNameUpdate);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const hotelMappingSchema = useSelector(selectSchemaMapping);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
  //
  useEffect(() => {
    // Set iPrefer access true for admin, super admin and preferred user roles
    dispatch(
      dashboardActions.setTabAccess({
        isIPrefer:
          userRole === PREFERRED_ADMIN_ROLE ||
          userRole === PREFERRED_ROLE ||
          userRole === ADMIN_ROLE ||
          userRole === SUPER_ADMIN_ROLE,
      })
    );
  }, []);
  //

  //
  const setSecondaryHotelSetOverTime = useSetSecondaryHotelSetOverTime();
  useEffect(() => {
    setSecondaryHotelSetOverTime(primaryHotelSetOverTime);
  }, [tabValue]);
  //
  useEffect(() => {
    const allBookingDatesAvailable =
      summaryBookingDate &&
      regionalMapBookingDate &&
      trendBookingDate &&
      paceBookingDate &&
      workbookBookingDate;
    //
    if (!allBookingDatesAvailable) {
      return;
    }
    const dashboardAction = dashboardActions;
    const workbookAction = workbookActions;
    const trendAction = trendActions;
    const paceAction = paceActions;
    if (!isEmpty(TAB_VALUE)) {
      evaluateHotelDateValidation({
        tabValue,
        selectedHotelName,
        summaryHotelFocusOn,
        workbookHotelFocusOn,
        trendHotelFocusOn,
        paceHotelFocusOn,
        targetDate,
        trendTargetDate,
        alignmentToggle,
        summaryDateComparison,
        trendDateComparison,
        trendAlignmentToggle,
        paceDateComparison,
        paceAlignmentToggle,
        workbookAlignmentToggle,
        workbookDateComparison,
        summaryCustomStartDate,
        summaryCustomEndDate,
        trendTimeCustomStartDate,
        trendTimeCustomEndDate,
        workbookCustomStartDate,
        workbookCustomEndDate,
        dashboardAction,
        workbookAction,
        trendAction,
        paceAction,
        dispatch,
        summarySymmetricComparisonToggle,
        paceSymmetricComparisonToggle,
        trendSymmetricComparisonToggle,
        workBookSymmetricComparisonToggle,
        summaryBookingDate,
        regionalMapBookingDate,
        trendBookingDate,
        paceBookingDate,
        workbookBookingDate,
        isSummaryCustomBooking,
        isPaceCustomBooking,
        isRegionCustomBooking,
        isTrendCustomBooking,
        isWorkbookCustomBooking,
        bookingPaceWindow,
        resetBookingDate,
        partialHours,
        lastBookingDate: lastPartialBookingDate,
        TAB_VALUE,
      });
    }
  }, [
    selectedHotelName,
    summaryHotelFocusOn,
    workbookHotelFocusOn,
    paceHotelFocusOn,
    trendHotelFocusOn,
    webClicksHotelFocusOn,
    tabValue,
    targetDate,
    trendTargetDate,
    alignmentToggle,
    summaryDateComparison,
    trendDateComparison,
    trendAlignmentToggle,
    paceDateComparison,
    paceAlignmentToggle,
    workbookAlignmentToggle,
    workbookDateComparison,
    summaryCustomDate,
    workbookCustomDate,
    trendTimeCustomDate,
    summaryCustomStartDate,
    summaryCustomEndDate,
    trendTimeCustomStartDate,
    trendTimeCustomEndDate,
    workbookCustomStartDate,
    workbookCustomEndDate,
    summaryBookingDate,
    regionalMapBookingDate,
    trendBookingDate,
    paceBookingDate,
    workbookBookingDate,
    summarySymmetricComparisonToggle,
    paceSymmetricComparisonToggle,
    trendSymmetricComparisonToggle,
    workBookSymmetricComparisonToggle,
    isSummaryCustomBooking,
    isPaceCustomBooking,
    isRegionCustomBooking,
    isTrendCustomBooking,
    isWorkbookCustomBooking,
    bookingPaceWindow,
    resetBookingDate,
    TAB_VALUE,
  ]);

  // check whether hotels in hotel set have same local currency
  const allEqual = (arr) => arr.every((val) => val?.localCurrency === arr[0]?.localCurrency);

  // Function to get the common currency of a selected hotel or hotel set
  const getCommonCurrency = (selectedHotel) => {
    if (selectedHotel && selectedHotel?.hotels) {
      // Hotel set
      if (allEqual(selectedHotel?.hotels)) {
        return selectedHotel?.hotels[0]?.localCurrency;
      }
      return null;
    }
    // Single hotel
    return selectedHotel?.localCurrency;
  };

  // Function to check isForecastAvailable for hotels or hotel sets
  const checkForecastAvailability = (item) => {
    if (item.hotels) {
      return item.hotels.every((hotel) => hotel.isForecastAvailable);
    }
    return item.isForecastAvailable;
  };

  // Determine tab wise forecast toggle value based on hotel forecast availability
  useEffect(() => {
    if (!isEmpty(selectedHotelName) && isHotelForecast != null) {
      switch (tabValue) {
        case TAB_VALUE.SUMMARY:
          dispatch(dashboardActions.setSummaryForecastFilter(isHotelForecast));
          break;
        case TAB_VALUE.TREND_TIME:
        case TAB_VALUE.PBI_TREND:
          dispatch(trendActions.setIsTrendForecast(false));
          break;
        case TAB_VALUE.PACE:
        case TAB_VALUE.PACE_TEST:
          dispatch(paceActions.setIsPaceForecast(false));
          break;
        default:
          break;
      }
    }
  }, [isHotelForecast, selectedHotelName, TAB_VALUE]);
  //
  const setHotelGroupName = () => {
    const { primary, secondary } = setUpdate;

    // check primary update
    if (primary?.updated) {
      const hotel = mergedHotelList?.find((hotels) => hotels?.id === primary?.name);
      if (hotel) {
        dispatch(dashboardActions.setHotelName(hotel));
      }
      dispatch(
        dashboardActions.setHotelSetUpdated({
          primary: { updated: false, name: null },
          secondary: { updated: false, name: null },
        })
      );
    }

    // check secondary update
    if (secondary?.updated) {
      const hotel = mergedHotelList?.find((hotels) => hotels?.id === secondary?.name);
      //
      if (hotel) {
        switch (tabValue) {
          case TAB_VALUE.SUMMARY:
            dispatch(summaryActions.setHotelFocusOn(hotel));
            break;
          case TAB_VALUE.WORKBOOK_REPORT:
            dispatch(workbookActions.setHotelFocusOn(hotel));
            break;
          case TAB_VALUE.TREND_TIME:
          case TAB_VALUE.PBI_TREND:
            dispatch(trendActions.setTrendHotelFocusOn(hotel));
            break;
          case TAB_VALUE.PACE:
          case TAB_VALUE.PACE_TEST:
            dispatch(paceActions.setHotelFocusOn(hotel));
            break;
          default:
            break;
        }
      }
      dispatch(
        dashboardActions.setHotelSetUpdated({
          primary: { updated: false, name: null },
          secondary: { updated: false, name: null },
        })
      );
    }
  };
  //
  useEffect(() => {
    setHotelGroupName();
  }, [mergedHotelList, tabValue, TAB_VALUE]);

  // Determine the local currency value and currency list on tab, primary or secondary hotel change
  useEffect(() => {
    let selectedTabFocusOn;
    if (
      selectedHotelName &&
      summaryHotelFocusOn &&
      workbookHotelFocusOn &&
      paceHotelFocusOn &&
      trendHotelFocusOn &&
      webClicksHotelFocusOn
    ) {
      //
      switch (tabValue) {
        case TAB_VALUE.SUMMARY:
          selectedTabFocusOn = summaryHotelFocusOn;
          break;
        case TAB_VALUE.WORKBOOK_REPORT:
          selectedTabFocusOn = workbookHotelFocusOn;
          break;
        case TAB_VALUE.TREND_TIME:
        case TAB_VALUE.PBI_TREND:
          selectedTabFocusOn = trendHotelFocusOn;
          break;
        case TAB_VALUE.WEB_CLICK:
          selectedTabFocusOn = webClicksHotelFocusOn;
          break;
        case TAB_VALUE.PACE:
        case TAB_VALUE.PACE_TEST:
          selectedTabFocusOn = paceHotelFocusOn;
          break;
        default:
          break;
      }
    }
    const primaryCurrency = selectedHotelName ? getCommonCurrency(selectedHotelName) : null;
    //
    if (
      tabValue === TAB_VALUE.REGION_MAP ||
      tabValue === TAB_VALUE.EXPORT_WORKBOOK_REPORT ||
      tabValue === TAB_VALUE.ALERT
    ) {
      if (primaryCurrency && primaryCurrency !== CURRENCY_TYPES.USD) {
        dashboardActions.setCurrencyFilter(getCurrencySchema(visualFilters, CURRENCY_TYPES.RC));
        dispatch(dashboardActions.setLocalCurrency(primaryCurrency));
        dispatch(dashboardActions.setLocalCurrencyList(primaryCurrency));
      } else {
        dispatch(dashboardActions.removeOtherLocalCurrenciesFromList());
      }
    } else {
      const secondaryCurrency = selectedTabFocusOn ? getCommonCurrency(selectedTabFocusOn) : null;
      if (
        primaryCurrency &&
        secondaryCurrency &&
        primaryCurrency === secondaryCurrency &&
        primaryCurrency !== CURRENCY_TYPES.USD
      ) {
        dispatch(
          dashboardActions.setCurrencyFilter(getCurrencySchema(visualFilters, CURRENCY_TYPES.RC))
        );
        dispatch(dashboardActions.setLocalCurrency(primaryCurrency));
        dispatch(dashboardActions.setLocalCurrencyList(primaryCurrency));
      } else {
        dispatch(dashboardActions.removeOtherLocalCurrenciesFromList());
        dispatch(
          dashboardActions.setCurrencyFilter(getCurrencySchema(visualFilters, CURRENCY_TYPES.USD))
        );
      }
      if (!isEmpty(selectedHotelName)) {
        const allForecastsAvailable = checkForecastAvailability(selectedHotelName);
        dispatch(
          dashboardActions.setHotelForecastDisabledText(getForecastMessage(selectedHotelName))
        );
        dispatch(dashboardActions.setIsHotelForecast(allForecastsAvailable));
      }
    }
  }, [
    selectedHotelName,
    summaryHotelFocusOn,
    workbookHotelFocusOn,
    paceHotelFocusOn,
    trendHotelFocusOn,
    webClicksHotelFocusOn,
    tabValue,
    TAB_VALUE,
  ]);
  //
  const isValidHotelLabel = (ff) => {
    const hotelPattern = /^Hotel \d+$/;
    return hotelPattern.test(ff.label);
  };
  //
  useEffect(() => {
    if (
      hotelList !== null &&
      hotelList?.length > 0 &&
      !isEmpty(hotelGroups) &&
      assignedHotelGroups !== null &&
      hotelMappingSchema &&
      !isEmpty(hotelMappingSchema)
    ) {
      let demoModeHotelList = [...hotelList];
      if (!isDemoUser && isDemoModeActivated && !isValidHotelLabel(demoModeHotelList[0])) {
        dispatch(dashboardActions.setOriginalHotelList(demoModeHotelList));
        demoModeHotelList = getHotelDemoModeFocusOnValue(isDemoModeActivated, demoModeHotelList);
      } else if (!isDemoUser && !isDemoModeActivated && isValidHotelLabel(demoModeHotelList[0])) {
        demoModeHotelList = getHotelDemoModeFocusOnValue(isDemoModeActivated, demoModeHotelList);
        dispatch(dashboardActions.resetHotelList(demoModeHotelList));
      }
      const hotelGroup = getHotelGroupFocusOnValue(hotelGroups, hotelList, hotelMappingSchema);
      const mergedArray = [
        ...(demoModeHotelList ?? []),
        ...(hotelGroup?.hotelGroupDropdownList ?? []),
        ...(assignedHotelGroups ?? []),
      ];
      const sortedByLabel = mergedArray?.sort((a, b) => {
        if (!isDemoUser && isDemoModeActivated) {
          // If both objects have the "hotels" attribute, use localeCompare
          if (a.hotels && b.hotels) {
            return a?.label?.localeCompare(b?.label);
          }
          // Otherwise, sort numerically based on the second word in the label
          return (
            a.label && b.label && Number(a.label.split(' ')[1]) - Number(b.label.split(' ')[1])
          );
        }
        // If isDemo is false, sort everything with localeCompare
        return a?.label?.localeCompare(b?.label);
      });
      const sortedByPredefined =
        [...sortedByLabel]?.sort((a, b) => {
          if (a.isPredefined === b.isPredefined) {
            return 0; // If both items have the same predefined status, they are considered equal
          }
          if (a.isPredefined) {
            return -1; // If a is predefined and b is not, a should come before b
          }
          return 1; // If b is predefined and a is not, b should come before a
        }) ?? [];
      const sortedByFavorite =
        [...sortedByPredefined]?.sort((a, b) => {
          if (a.isFavorite === b.isFavorite) {
            return 0; // If both items have the same favorite status, they are considered equal
          }
          if (a.isFavorite) {
            return -1; // If a is favorite and b is not, a should come before b
          }
          return 1; // If b is favorite and a is not, b should come before a
        }) ?? [];
      if (!selectedHotelName) {
        dispatch(dashboardActions.setHotelName(sortedByFavorite[0]));
        dispatch(dashboardActions.setOriginalHotelName(sortedByFavorite[0]));
      }
      dispatch(dashboardActions.setPredefinedHotelList(assignedHotelGroups));
      dispatch(dashboardActions.setHotelGroupDropdownList(hotelGroup?.hotelGroupDropdownList));
      dispatch(dashboardActions.setMergedHotelList(sortedByFavorite));

      if (isSelectedHotelSetModified) {
        dispatch(
          dashboardActions.setSelectedHotelGroup({ ...hotelGroup, hotelList: sortedByFavorite })
        );
        dispatch(dashboardActions.setIsHotelSetModified(false));
      }
      if (!isSignIn && serializedState && !isEmpty(selectedHotelName)) {
        return;
      }
      dispatch(
        dashboardActions.setSelectedHotelGroup({ ...hotelGroup, hotelList: sortedByFavorite })
      );
    }
    if (
      hotelList !== null &&
      hotelList?.length <= 0 &&
      assignedHotelGroups !== null &&
      userRole === PREFERRED_ROLE
    ) {
      const mergedArray = [...(assignedHotelGroups ?? [])];
      const sortedByLabel = mergedArray?.sort((a, b) => a?.label?.localeCompare(b?.label));
      if (!selectedHotelName) {
        dispatch(dashboardActions.setHotelName(sortedByLabel[0]));
        dispatch(dashboardActions.setOriginalHotelName(sortedByLabel[0]));
      }
      dispatch(dashboardActions.setPredefinedHotelList(mergedArray));
      dispatch(dashboardActions.setMergedHotelList(sortedByLabel));
    }
  }, [
    hotelList,
    hotelGroups,
    assignedHotelGroups,
    isDemoUser,
    isSignIn,
    isSelectedHotelSetModified,
    userRole,
    isDemoModeActivated,
    hotelMappingSchema,
  ]);
  // Triggers when demo mode changes to set hotelName
  useEffect(() => {
    if (
      !isDemoUser &&
      isDemoModeActivated &&
      selectedHotelName &&
      !isValidHotelLabel(selectedHotelName)
    ) {
      const demoModeOnHotelName = {
        ...selectedHotelName,
        label: selectedHotelName?.altName,
      };
      if (demoModeOnHotelName && !('hotels' in demoModeOnHotelName)) {
        dispatch(dashboardActions.setHotelName(demoModeOnHotelName));
      }
    }

    if (
      !isDemoUser &&
      !isDemoModeActivated &&
      selectedHotelName &&
      isValidHotelLabel(selectedHotelName)
    ) {
      const demoModeOffHotelName = {
        ...selectedHotelName,
        label: selectedHotelName?.name,
      };
      if (demoModeOffHotelName && !('hotels' in demoModeOffHotelName)) {
        dispatch(dashboardActions.setHotelName(demoModeOffHotelName));
      }
    }
  }, [isDemoModeActivated]);
};
//
export default UseTabContainer;
